import clsx from 'clsx';
import { CheckCircle2, Circle, Lightbulb, Target } from 'lucide-react';
import { useEffect, useState } from 'react';

import { type ExploreChallenge, metChallengeIds } from './modelInputContract';

/**
 * Teaching goal for an Explore view (CT-INTERACT C7): one learning question and a short list of
 * "Try this" tasks. A task ticks the moment the learner's own setup meets it, then stays ticked and
 * shows why it works. Nothing is saved or scored; this is guidance, not assessment evidence.
 */
export function ExploreChallenges<R>({
  question,
  challenges,
  result,
}: {
  question: string;
  challenges: readonly ExploreChallenge<R>[];
  result: R | null;
}) {
  const [done, setDone] = useState<string[]>([]);
  const metNow = metChallengeIds(challenges, result);
  const metKey = metNow.join('|');

  useEffect(() => {
    if (!metKey) return;
    setDone((current) => {
      const next = metKey.split('|').filter((id) => !current.includes(id));
      return next.length ? [...current, ...next] : current;
    });
  }, [metKey]);

  const doneCount = challenges.filter((challenge) => done.includes(challenge.id)).length;

  return (
    <section aria-label="Learning question and tasks" className="rounded-3xl border border-brand-mint/30 bg-wash-mint p-4">
      <p className="flex items-start gap-2 text-base font-extrabold text-ink">
        <Lightbulb className="mt-0.5 h-5 w-5 shrink-0 text-brand-mint" aria-hidden="true" />
        <span>{question}</span>
      </p>
      <div className="mt-3 flex items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-[0.12em] text-slate2">
          <Target className="h-4 w-4" aria-hidden="true" /> Try this
        </p>
        <p className="text-xs font-bold text-slate2" aria-live="polite" data-testid="challenge-progress">
          {doneCount} of {challenges.length} done
        </p>
      </div>
      <p className="mt-1 text-xs font-semibold leading-5 text-slate2" data-testid="challenge-how">
        These are goals, not buttons: change your setup and each one ticks by itself.
      </p>
      <ol className="mt-2 grid gap-2">
        {challenges.map((challenge, index) => {
          const isDone = done.includes(challenge.id);
          const Icon = isDone ? CheckCircle2 : Circle;
          return (
            <li
              key={challenge.id}
              data-testid={`challenge-${challenge.id}`}
              data-done={isDone}
              className={clsx(
                'rounded-2xl border px-3 py-2',
                isDone ? 'border-brand-mint/40 bg-canvas-pure' : 'border-hairline bg-canvas-pure/70',
              )}
            >
              <p className="flex items-start gap-2 text-sm font-extrabold text-ink">
                <Icon className={clsx('mt-0.5 h-4 w-4 shrink-0', isDone ? 'text-brand-mint' : 'text-slate2')} aria-hidden="true" />
                <span>
                  {index + 1}. {challenge.prompt}
                  {isDone ? <span className="sr-only"> (done)</span> : null}
                </span>
              </p>
              {isDone ? (
                <p className="mt-1 pl-6 text-xs font-semibold leading-5 text-ink-soft">{challenge.why}</p>
              ) : challenge.hint ? (
                <p className="mt-1 pl-6 text-xs font-medium leading-5 text-slate2">Hint: {challenge.hint}</p>
              ) : null}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
